import {
  Badge,
  Button,
  Checkbox,
  confirm,
  createSortableColumn,
  DataTable,
  Label,
  type TanstackReactTable,
} from "@devhop/ui";
import { CheckIcon, EditIcon, EyeIcon, TrashIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { PackageTemplateDTO } from "@/modules/packages/domain/contracts";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type PackageTemplateRow = PackageTemplateDTO & { rulesCount: number };

type PackageTemplatesTableProps = {
  data: PackageTemplateRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (template: PackageTemplateRow) => void;
  onEdit: (template: PackageTemplateRow) => void;
  onDelete: (id: string) => void;
  onBulkDelete?: (ids: string[]) => void;
  onBulkToggleStatus?: (ids: string[], status: "active" | "inactive") => void;
};

export function PackageTemplatesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onBulkToggleStatus,
}: PackageTemplatesTableProps) {
  const [columnSelection, setColumnSelection] = useState<string[]>([]);

  // Clear selection when data changes (pagination, filter, etc.)
  useEffect(() => {
    setColumnSelection([]);
  }, [data, offset]);

  const selectedCount = columnSelection.length;
  const allSelected = data.length > 0 && selectedCount === data.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setColumnSelection(data.map((row) => row.id));
    } else {
      setColumnSelection([]);
    }
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedCount === 0) return;

    const ok = await confirm({
      title: "ລຶບ template ແພັກເກດຫຼາຍລາຍການ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ແພັກເກດ ${selectedCount} ລາຍການ?`,
      actionText: "ລຶບ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (ok) {
      onBulkDelete(columnSelection);
      setColumnSelection([]);
    }
  };

  const handleBulkToggleStatus = async (status: "active" | "inactive") => {
    if (!onBulkToggleStatus || selectedCount === 0) return;

    const statusLabel = status === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";
    const ok = await confirm({
      title: `ປ່ຽນສະຖານະ template ແພັກເກດ ${selectedCount} ລາຍການ`,
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະ${statusLabel} template ແພັກເກດ ${selectedCount} ລາຍການ?`,
      actionText: statusLabel,
    });

    if (ok) {
      onBulkToggleStatus(columnSelection, status);
      setColumnSelection([]);
    }
  };

  const columns: TanstackReactTable.ColumnDef<PackageTemplateRow>[] = [
    {
      enableSorting: false,
      accessorKey: "id",
      header: () => (
        <Checkbox
          checked={allSelected}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
          className="hidden md:block"
        />
      ),
      size: 50,
      cell: ({ row }) => (
        <Label className="flex items-center gap-2">
          <Checkbox
            checked={columnSelection.includes(row.original.id)}
            onCheckedChange={(checked) => {
              if (checked) {
                setColumnSelection([...columnSelection, row.original.id]);
              } else {
                setColumnSelection(
                  columnSelection.filter((id) => id !== row.original.id),
                );
              }
            }}
            aria-label={`Select ${row.original.name}`}
          />
          <span className="hidden font-medium text-sm max-md:block">ເລືອກ</span>
        </Label>
      ),
    },
    createSortableColumn("name", "ຊື່ແພັກເກດ"),
    {
      header: "ຈຳນວນຄັ້ງ",
      accessorKey: "totalSessions",
      cell: ({ row }) => (
        <div className="text-right">{row.original.totalSessions}</div>
      ),
    },
    {
      header: "ຈຳນວນວັນ",
      accessorKey: "validDays",
      cell: ({ row }) => (
        <div className="text-right">{row.original.validDays}</div>
      ),
    },
    {
      header: "ລາຄາ (ກີບ)",
      accessorKey: "price",
      cell: ({ row }) => (
        <div className="text-right">{row.original.price.toLocaleString()}</div>
      ),
    },
    {
      header: "ກົດລະບຽບທີ່ອະນຸຍາດ",
      accessorKey: "rulesCount",
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.rulesCount > 0
            ? `${row.original.rulesCount} ກົດລະບຽບ`
            : "-"}
        </div>
      ),
    },
    {
      header: "ເລືອກໄດ້",
      accessorKey: "maxSelectableClasses",
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.maxSelectableClasses != null &&
          row.original.maxSelectableClasses >= 1
            ? `${row.original.maxSelectableClasses} ຄລາດສ໌`
            : "-"}
        </div>
      ),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === "active" ? "default" : "secondary"}>
            {status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
          </Badge>
        );
      },
    },
    {
      header: "ວັນທີສ້າງ",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: PackageTemplateRow } }) => {
        const { id } = row.original;
        const actions = [];

        actions.push({
          label: "ລາຍລະອຽດ",
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => onView(row.original),
        });

        actions.push({
          label: "ແກ້ໄຂ",
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => onEdit(row.original),
        });

        actions.push({
          label: "ລຶບ",
          variant: "destructive" as const,
          icon: <TrashIcon className="h-4 w-4" />,
          onClick: async () => {
            const ok = await confirm({
              title: "ລຶບ template ແພັກເກດ",
              description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ແພັກເກດນີ້?",
              actionText: "ລຶບ",
              ActionProps: {
                variant: "destructive",
              },
            });
            if (ok) onDelete(id);
          },
        });

        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <>
      {selectedCount > 0 && (
        <div className="flex flex-col gap-2 border-b bg-muted/50 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              ເລືອກ {selectedCount} ລາຍການ
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setColumnSelection([])}
            >
              <XIcon className="size-4" />
              <span className="hidden sm:inline">ຍົກເລີກ</span>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onBulkToggleStatus && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("active")}
                  className="flex-1 sm:flex-initial"
                >
                  <CheckIcon className="size-4" />
                  <span className="hidden sm:inline">ເປີດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ເປີດ</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("inactive")}
                  className="flex-1 sm:flex-initial"
                >
                  <XIcon className="size-4" />
                  <span className="hidden sm:inline">ປິດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ປິດ</span>
                </Button>
              </>
            )}
            {onBulkDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                className="flex-1 sm:flex-initial"
              >
                <TrashIcon className="size-4" />
                <span className="hidden sm:inline">ລຶບ ({selectedCount})</span>
                <span className="sm:hidden">ລຶບ</span>
              </Button>
            )}
          </div>
        </div>
      )}
      <DataTable<PackageTemplateRow, unknown>
        noDataMessage="ບໍ່ພົບ template ແພັກເກດ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </>
  );
}
